import { Card, Heading, Icon, NotificationCounter, NotificationCounterVariant } from '@televet/kibble-ui';
import { Box, Collapse, Divider, Flex, useDisclosure } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { formatTimeToReadable } from 'pages/Conversations/utils/formatDate';
import { PendingTaskFields, TaskList, TaskSortingKeys, TaskWithMemoizedValues } from 'pages/Tasks/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import usePendingTasksCount from 'shared/hooks/usePendingTasksCount';
import { useTracking } from 'shared/providers/TrackingProvider';
import { TasksEvents } from 'shared/providers/TrackingProvider/EventNames';
import { TaskStatus, TaskType, useFindManyTaskQuery, useFindTaskCountIdsAndStatusesQuery } from 'shared/types/graphql';
import { useTasksContext } from '../../context/TasksContext';
import { refineTaskFilterWithStatuses } from '../../utils';
import CustomTaskModal from '../CustomTaskModal';
import { TaskTable } from '../TaskTable';

const fortyEightHours = 48 * 60 * 60 * 1000;

type TaskGroupListCardProps = {
  taskList: TaskList;
  taskStatuses: TaskStatus[];
  completedTasksOnly?: boolean;
};

const taskBelongsToList = (taskList: TaskList, task: PendingTaskFields): boolean => {
  if (taskList.taskFilters[0].taskType === TaskType.WidgetRequest && task.type === TaskType.WidgetRequest) {
    return taskList.taskFilters.some(
      (filter) => filter.metadata?.entities?.[0].entityId === task.widgetRequest?.clinicWidgetRequestType?.id,
    );
  }

  if (taskList.taskFilters[0].taskType === TaskType.Automation && task.type === TaskType.Automation) {
    return true;
  }
 
  if (
    [TaskType.Automation, TaskType.Custom].includes(taskList.taskFilters[0].taskType) &&
    task.type === taskList.taskFilters[0].taskType
  ) {
    return true;
  }

  return false;
};

export const TaskGroupListCard = ({
  taskList,
  taskStatuses,
  completedTasksOnly,
}: TaskGroupListCardProps): JSX.Element => {
  const isTasksCustomList = taskList.taskFilters[0].taskType === TaskType.Custom;
  const { whereFilter, toggleTaskListCollapsibleState, expandedTaskListIds, updateSorting, getSortCriteriaForList } =
    useTasksContext();

  const { updatedTask } = usePendingTasksCount();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = useState<number>(5);
  const [editingTask, setEditingTask] = useState<TaskWithMemoizedValues | undefined>(undefined);

  const { localOrderBy, queryOrderBy } = useMemo(
    () => getSortCriteriaForList(taskList.id),
    [getSortCriteriaForList, taskList.id],
  );

  const { isOpen, onToggle: _onToggle } = useDisclosure({
    isOpen: expandedTaskListIds.includes(taskList.id),
  });
  const { currentClinicId } = useClinicUser();
  const [completedAt, setCompletedAt] = useState<Date | undefined>(
    completedTasksOnly && !isTasksCustomList ? new Date(new Date().getTime() - fortyEightHours) : undefined,
  );

  const { trackEvent } = useTracking();

  const {
    isOpen: isCustomTaskModalOpen,
    onOpen: _onCustomTaskModalOpen,
    onClose: _onCustomTaskModalClose,
  } = useDisclosure();

  const onCustomTaskModalOpen = useCallback((): void => {
    _onCustomTaskModalOpen();
    trackEvent(TasksEvents.CustomTaskAddNewButtonClicked, {
      clinicId: currentClinicId,
    });
  }, [_onCustomTaskModalOpen, currentClinicId, trackEvent]);

  const onCustomTaskModalClose = useCallback((): void => {
    _onCustomTaskModalClose();
    setEditingTask(undefined);
  }, [_onCustomTaskModalClose]);

  const onTaskEdit = useCallback(
    (task: TaskWithMemoizedValues): void => {
      setEditingTask(task);
      onCustomTaskModalOpen();
    },
    [onCustomTaskModalOpen],
  );

  const where = useMemo(
    () => refineTaskFilterWithStatuses(whereFilter, taskStatuses, taskList, currentClinicId, completedAt),
    [completedAt, taskList, taskStatuses, whereFilter, currentClinicId],
  );

  const {
    data: taskCountData,
    previousData: previousCount,
    refetch: refetchCount,
  } = useFindTaskCountIdsAndStatusesQuery({
    variables: {
      where,
    },
    skip: !where,
    fetchPolicy: GraphQLFetchPolicies.NoCache,
  });

  const {
    data: tasksData,
    loading: isTaskDataLoading,
    refetch: getTasks,
  } = useFindManyTaskQuery({
    variables: {
      where,
      orderBy: queryOrderBy,
      take: currentPageSize,
      skip: currentPageSize * (currentPage - 1),
    },
    skip: !isOpen,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const hasNewTasks = useMemo(
    () => taskCountData?.findManyTask?.some(({ status }) => status === TaskStatus.Open),
    [taskCountData],
  );

  const tasks = useMemo(() => {
    const _tasks: TaskWithMemoizedValues[] = (tasksData?.findManyTask || []).map((task) => {
      return {
        ...task,
        assigneeIds: task.assignees.map((assignee) => assignee.id),
        'clinicPetParent.firstName': task.clinicPetParent
          ? `${task.clinicPetParent.firstName} ${task.clinicPetParent.lastName}`
          : '',
        createdAtReadableTime: formatTimeToReadable(new Date(task.createdAt)),
        completedAtReadableTime: task?.completedAt ? formatTimeToReadable(new Date(task.completedAt)) : undefined,
      };
    });
    return _tasks;
  }, [tasksData]);

  const onToggle = (): void => {
    _onToggle();
    toggleTaskListCollapsibleState(taskList.id, !isOpen);
  };

  const onPageChange = useCallback((page: number): void => {
    setCurrentPage(page);
  }, []);

  const onPageSizeChange = useCallback(
    (pageSize: number) => {
      onPageChange(1);
      setCurrentPageSize(pageSize);
    },
    [onPageChange, setCurrentPageSize],
  );

  const onSortChange = useCallback(
    (key: TaskSortingKeys): void => {
      updateSorting(key, taskList.id);
      setCompletedAt(completedTasksOnly ? new Date(new Date().getTime() - fortyEightHours) : undefined);
    },
    [completedTasksOnly, taskList.id, updateSorting],
  );

  const refetch = useCallback(() => {
    getTasks();
    refetchCount();
  }, [getTasks, refetchCount]);

  useEffect(() => {
    if (!completedTasksOnly && updatedTask && taskBelongsToList(taskList, updatedTask)) {
      if (updatedTask.status !== TaskStatus.Completed) {
        refetch();
      } else {
        setTimeout(() => {
          // Give some time for the task finish its animation
          refetch();
        }, 1100);
      }
    }
  }, [completedTasksOnly, updatedTask, refetch, taskList]);

  return (
    <Card p={0} pb={2} data-testid={`task-list-card-${taskList.id}`}>
      <Flex justifyContent="space-between">
        <Flex
          p={5}
          cursor="pointer"
          onClick={onToggle}
          flex={1}
          data-testid={`task-list-card-toggle-${taskList.id}`}
          justifyContent="space-between"
        >
          <Flex alignItems="center" gap={1} height="40px">
            <Icon name={isOpen ? 'chevronDown' : 'chevronRight'} size="sm" />
            <Heading size="sm" color={taskList.isInactiveOrDeleted ? 'text.subtle' : undefined}>
              {taskList.name}
              {taskCountData?.findManyTask.length ? ` (${taskCountData?.findManyTask.length})` : ''}
            </Heading>

            {!completedTasksOnly && hasNewTasks && (
              <NotificationCounter
                size="xs"
                variant={NotificationCounterVariant.Red}
                data-testid={`tasks-list-counter-${taskList.id}`}
              />
            )}
          </Flex>
          {!completedTasksOnly && isTasksCustomList && (
            <Button
              iconName="plus"
              size="md"
              onClick={(ev): void => {
                ev.preventDefault();
                ev.stopPropagation();
                onCustomTaskModalOpen();
              }}
              data-testid="tasks-add-new-button"
              variant="tertiary"
            >
              New Task
            </Button>
          )}
        </Flex>
      </Flex>
      <Divider borderColor={taskList.color} borderWidth="1px" width="unset" />
      <Collapse in={isOpen} animateOpacity>
        <Box overflow="auto">
          {isOpen && (
            <TaskTable
              taskListId={taskList.id}
              tasks={tasks}
              onSort={onSortChange}
              currentSort={localOrderBy}
              borderLeftColor={taskList.color}
              completedTasksOnly={completedTasksOnly}
              onRemoveElement={refetch}
              paginationProps={{
                recordCount: taskCountData?.findManyTask?.length || previousCount?.findManyTask?.length || 0,
                currentPage: currentPage,
                onPageChange: onPageChange,
                onPageSizeChange: onPageSizeChange,
                pageSize: currentPageSize,
                isLoading: isTaskDataLoading,
              }}
              onStatusChange={refetch}
              onTaskEdit={isTasksCustomList ? onTaskEdit : undefined}
            />
          )}
        </Box>
      </Collapse>
      {isCustomTaskModalOpen && (
        <CustomTaskModal
          isOpen={isCustomTaskModalOpen}
          onClose={onCustomTaskModalClose}
          existingTask={editingTask}
          onAction={refetch}
        />
      )}
    </Card>
  );
};
